// Publishes an explicit "this view has finished rendering" signal in the DOM so
// automated tests can wait on it instead of racing a spinner. The spinner is
// mounted on one screen only and disappears before late data arrives.
//
// Deliberately outside React: index.js wraps RouterProvider in GlobalLayout, so
// there is no router context above the app to read a location from, StrictMode
// double-invokes effects in development, and the login, to-login and
// period-selector routes sit outside Protected.
//
// A view counts as ready when all three hold: no counted request is in flight,
// nothing outside [data-ready-ignore] has mutated inside #root for QUIET_MS, and
// a frame has been painted since. Network quiet on its own is not enough: with no
// query library the app chains fetches through useEffect, so the counter dips to
// zero mid-waterfall. DOM quiet on its own is not enough either, because a screen
// can sit still while its first request is still open.

const QUIET_MS = 200;
const IGNORE_SELECTOR = '[data-ready-ignore]';
const OBSERVE_OPTIONS = { childList: true, subtree: true, characterData: true, attributes: true };

let pending = 0;
let quietTimer = null;
let observer = null;
let rootEl = null;
let appState = 'ready';
let isReady = false;
let held = false;

function afterPaint(fn) {
  requestAnimationFrame(() => requestAnimationFrame(fn));
}

function cancelQuietTimer() {
  if (quietTimer !== null) {
    clearTimeout(quietTimer);
    quietTimer = null;
  }
}

function publish() {
  isReady = true;
  document.body.dataset.appState = appState;
  document.body.dataset.appReady = 'true';
  // Post-ready DOM churn must not move the flag; only a counted request does
  // (see requestStarted). Background polls opt out in helpers/axios.js.
  if (observer) observer.disconnect();
}

// Armed only while the counter is at zero. A dip to zero between two links of a
// waterfall starts the timer and the next request cancels it again, so the timer
// only ever completes when the view has genuinely stopped working.
function arm() {
  if (isReady || held) return;
  cancelQuietTimer();
  if (pending > 0) return;

  quietTimer = setTimeout(() => {
    quietTimer = null;
    afterPaint(() => {
      if (!isReady && pending === 0) publish();
    });
  }, QUIET_MS);
}

function onMutations(records) {
  if (isReady) return;

  for (const record of records) {
    const target = record.target;
    const el = target.nodeType === Node.ELEMENT_NODE ? target : target.parentElement;
    if (el && el.closest(IGNORE_SELECTOR)) continue;
    arm();
    return;
  }
}

function observe() {
  if (!observer || !rootEl) return;
  observer.disconnect();
  observer.observe(rootEl, OBSERVE_OPTIONS);
}

// A counted request on a view that is already ready (filter, sort, pagination,
// save) reloads it in place without a URL change, so take the flag down and
// publish again once it has settled. While held, the reload owns the flag.
export function requestStarted() {
  pending += 1;
  if (isReady && !held) resetReadyFlag();
  else arm();
}

export function requestSettled() {
  pending = Math.max(0, pending - 1);
  arm();
}

// The view could not show its content: the error boundary caught, the server
// failed, or the app was unreachable. A 422 on a form submit is not this.
export function markAppError() {
  appState = 'error';
  if (isReady) document.body.dataset.appState = appState;
}

// A period or module switch runs two requests and only then reloads the document.
// Clearing alone is not enough there: the flag would re-publish during that window
// and a test would assert against the page that is about to be thrown away. Hold
// it down until the reload takes over, and release only if the switch failed and
// the old page stays.
export function holdReadyFlag() {
  held = true;
  resetReadyFlag();
}

export function releaseReadyFlag() {
  held = false;
  arm();
}

export function resetReadyFlag() {
  isReady = false;
  appState = 'ready';
  delete document.body.dataset.appReady;
  delete document.body.dataset.appState;
  cancelQuietTimer();
  observe();
  arm();
}

export function startReadyFlag(router) {
  rootEl = document.getElementById('root');
  if (!rootEl) return;

  // A test that times out waiting for the flag needs to see why. Read-only.
  window.__pebReady = () => ({ pending, isReady, held, appState, quietTimerArmed: quietTimer !== null });

  observer = new MutationObserver(onMutations);
  observe();

  // Period and module switches need no handling here: both reload the document
  // (components/Layout/User.js), so the flag dies with the page. Only in-app
  // navigation has to be caught.
  if (router && typeof router.subscribe === 'function') {
    let last = null;
    router.subscribe((state) => {
      const next = `${state.location.pathname}${state.location.search}`;
      if (last !== null && next !== last) resetReadyFlag();
      last = next;
    });
  }

  arm();
}
